const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

const toCents = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'string' && value.includes('.')) return Math.round(parseFloat(value) * 100);
  return Number(value);
};

const formatNumber = (cents, precision, thousands, decimal) => {
  const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
  const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
  return fraction ? `${grouped}${decimal}${fraction}` : grouped;
};

const formatMoney = (cents, format) =>
  (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => {
    switch (name) {
      case 'amount_no_decimals':
        return formatNumber(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return formatNumber(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatNumber(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return formatNumber(cents, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        return formatNumber(cents, 0, ' ', '.');
      case 'amount_with_space_separator':
        return formatNumber(cents, 2, ' ', ',');
      case 'amount_with_period_and_space_separator':
        return formatNumber(cents, 2, ' ', '.');
      default:
        return formatNumber(cents, 2, ',', '.');
    }
  });

const imageSource = (product, width) => {
  const source =
    product.featured_image?.url || product.featured_image || product.images?.[0]?.src || product.images?.[0] || '';
  if (!source || typeof source !== 'string') return '';
  return `${source}${source.includes('?') ? '&' : '?'}width=${width}`;
};

const UPSELL_PROPERTIES = { _source: 'cart_upsell' };

const variantValue = (variant, index) => variant[`option${index + 1}`] ?? variant.options?.[index] ?? null;

const PLUS_ICON =
  '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M8 3v10M3 8h10"/></svg>';

const CLOSE_ICON =
  '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9"/></svg>';

class CartRecommendations extends HTMLElement {
  #dialog = null;
  #timer = null;
  #observer = null;
  #signature = '';
  #userClosed = false;
  #autoOpened = false;
  #requestId = 0;
  #products = new Map();
  #sheetState = null;

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  get #root() {
    return this.dataset.root || '/';
  }

  connectedCallback() {
    this.#dialog = this.closest('dialog');
    this.addEventListener('click', this.#onPanelClick);
    this.addEventListener('pointerdown', this.#stop);
    this.addEventListener('mousedown', this.#stop);
    document.addEventListener('click', this.#onDocumentClick);
    if (!this.#dialog) return;
    this.#observer = new MutationObserver(() => this.#onDialogChange());
    this.#observer.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });
    if (this.#dialog.open) this.#onDialogChange();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#observer?.disconnect();
    document.removeEventListener('click', this.#onDocumentClick);
  }

  async open() {
    if (this.isOpen) return;
    clearTimeout(this.#timer);
    this.#setOpen(true);
    await this.#load();
  }

  close(byUser = false) {
    if (byUser) this.#userClosed = true;
    this.#setOpen(false);
  }

  toggle() {
    if (this.isOpen) this.close(true);
    else this.open();
  }

  #stop = (event) => event.stopPropagation();

  #setOpen(state) {
    if (!state) this.#closeSheet();
    this.toggleAttribute('data-open', state);
    this.setAttribute('aria-hidden', String(!state));
    this.#dialog?.toggleAttribute('data-reco-open', state);
    this.#dialog
      ?.querySelectorAll('[data-cart-reco-trigger]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', String(state)));
  }

  #onDialogChange() {
    clearTimeout(this.#timer);
    if (this.#dialog.open) {
      this.#userClosed = false;
      this.#autoOpened = false;
      this.#scheduleAutoOpen();
    } else {
      this.#setOpen(false);
    }
  }

  #scheduleAutoOpen() {
    if (this.dataset.autoOpen !== 'true' || this.#userClosed || this.#autoOpened) return;
    const delay = Math.max(2, Number(this.dataset.autoOpenDelay) || 8) * 1000;
    this.#timer = setTimeout(() => this.#autoOpen(), delay);
  }

  #autoOpen() {
    if (!this.#dialog?.open || this.isOpen || this.#userClosed) return;
    if (!this.#dialog.querySelector('[data-cart-reco-trigger]')) {
      this.#scheduleAutoOpen();
      return;
    }
    const active = document.activeElement;
    if (active && this.#dialog.contains(active) && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) {
      this.#timer = setTimeout(() => this.#autoOpen(), 3000);
      return;
    }
    this.#autoOpened = true;
    this.open();
  }

  #onDocumentClick = (event) => {
    const trigger = event.target.closest?.('[data-cart-reco-trigger]');
    if (!trigger || !this.#dialog?.contains(trigger)) return;
    this.toggle();
  };

  #onPanelClick = (event) => {
    event.stopPropagation();
    const target = event.target;
    if (target.closest('[data-reco-close]')) {
      this.close(true);
      return;
    }
    if (target.closest('[data-reco-sheet-close], [data-reco-scrim]')) {
      this.#closeSheet();
      return;
    }
    const chip = target.closest('[data-reco-chip]');
    if (chip) {
      this.#select(Number(chip.dataset.index), chip.dataset.value);
      return;
    }
    const confirm = target.closest('[data-reco-confirm]');
    if (confirm) {
      this.#confirmSheet(confirm);
      return;
    }
    const quick = target.closest('[data-reco-quick]');
    if (quick) {
      this.#openSheet(Number(quick.dataset.recoQuick));
      return;
    }
    const add = target.closest('[data-reco-add]');
    if (add) this.#add(add);
  };

  #showStatus(text) {
    const status = this.querySelector('[data-reco-status]');
    if (!status) return;
    status.textContent = text || '';
    status.hidden = !text;
  }

  #renderSkeleton() {
    const list = this.querySelector('[data-reco-list]');
    this.#showStatus('');
    list.innerHTML = Array.from(
      { length: 4 },
      () =>
        `<li class="cart-reco__card cart-reco__card--skeleton" aria-hidden="true">
        <div class="cart-reco__row">
          <span class="cart-reco__media"></span>
          <div class="cart-reco__info"><span class="cart-reco__line"></span><span class="cart-reco__line cart-reco__line--short"></span></div>
        </div>
      </li>`
    ).join('');
  }

  async #load() {
    const requestId = ++this.#requestId;
    const list = this.querySelector('[data-reco-list]');
    let cart;
    try {
      const response = await fetch(`${this.#root}cart.js`, { headers: { Accept: 'application/json' } });
      cart = await response.json();
    } catch (error) {
      this.#showStatus(this.dataset.errorText);
      return;
    }
    const productIds = cart.items.map((item) => item.product_id);
    const signature = productIds.join(',');
    if (signature === this.#signature && list.children.length) return;
    if (!productIds.length) {
      list.innerHTML = '';
      this.#showStatus(this.dataset.emptyText);
      return;
    }
    this.#renderSkeleton();
    const products = await this.#fetchProducts(productIds[0], productIds);
    if (requestId !== this.#requestId) return;
    this.#signature = signature;
    if (!products.length) {
      list.innerHTML = '';
      this.#showStatus(this.dataset.emptyText);
      return;
    }
    this.#products = new Map(products.map((product) => [product.id, product]));
    list.innerHTML = products.map((product, index) => this.#renderCard(product, index)).join('');
  }

  async #fetchProducts(seed, exclude) {
    const limit = Number(this.dataset.limit) || 6;
    const primary = this.dataset.intent === 'complementary' ? 'complementary' : 'related';
    const intents = [primary, primary === 'related' ? 'complementary' : 'related'];
    for (const intent of intents) {
      try {
        const response = await fetch(
          `${this.#root}recommendations/products.json?product_id=${seed}&limit=10&intent=${intent}`,
          { headers: { Accept: 'application/json' } }
        );
        if (!response.ok) continue;
        const { products = [] } = await response.json();
        const filtered = products
          .filter((product) => !exclude.includes(product.id) && product.available !== false)
          .slice(0, limit);
        if (filtered.length) return filtered;
      } catch (error) {
        continue;
      }
    }
    return [];
  }

  #renderCard(product, index) {
    const format = this.dataset.moneyFormat;
    const variants = product.variants || [];
    const first = variants.find((variant) => variant.available !== false) || variants[0];
    const price = toCents(product.price ?? first?.price);
    const compare = toCents(product.compare_at_price ?? first?.compare_at_price);
    const single = variants.length <= 1;
    const title = escapeHtml(product.title);
    const url = escapeHtml(product.url || `/products/${product.handle}`);
    const image = imageSource(product, 240);
    const addLabel = escapeHtml(this.dataset.addLabel || 'Add to cart');
    const chooseLabel = escapeHtml(this.dataset.chooseLabel || 'Choose options');

    const action = single
      ? `<button type="button" class="cart-reco__add" data-reco-add="${first?.id}" aria-label="${addLabel}: ${title}">${PLUS_ICON}</button>`
      : `<button type="button" class="cart-reco__add" data-reco-quick="${product.id}" aria-haspopup="dialog" aria-label="${chooseLabel}: ${title}">${PLUS_ICON}</button>`;

    return `<li class="cart-reco__card" data-product-id="${product.id}" style="--i:${index}">
      <div class="cart-reco__row">
        <a class="cart-reco__media" href="${url}" tabindex="-1" aria-hidden="true">${
          image ? `<img src="${escapeHtml(image)}" alt="" width="64" height="80" loading="lazy">` : ''
        }</a>
        <div class="cart-reco__info">
          <a class="cart-reco__name" href="${url}">${title}</a>
          <p class="cart-reco__price"><span>${formatMoney(price, format)}</span>${
            compare > price ? `<s>${formatMoney(compare, format)}</s>` : ''
          }</p>
        </div>
        ${action}
      </div>
    </li>`;
  }

  #optionsFor(product) {
    const variants = product.variants || [];
    let names = (product.options || [])
      .map((option) => (typeof option === 'string' ? option : option?.name))
      .filter(Boolean);
    if (!names.length) {
      names = [1, 2, 3]
        .filter((position) => variants.some((variant) => variant[`option${position}`]))
        .map((position) => `Option ${position}`);
    }
    return names.map((name, index) => ({
      name,
      index,
      values: [
        ...new Set(
          variants.map((variant) => variantValue(variant, index)).filter((value) => value !== null && value !== undefined)
        ),
      ],
    }));
  }

  #matchVariant(state) {
    if (state.selected.some((value) => value === null)) return null;
    return (
      state.product.variants.find((variant) =>
        state.options.every((option) => variantValue(variant, option.index) === state.selected[option.index])
      ) || null
    );
  }

  #isAvailable(option, value) {
    const { product, options, selected } = this.#sheetState;
    return product.variants.some(
      (variant) =>
        variant.available !== false &&
        variantValue(variant, option.index) === value &&
        options.every(
          (other) =>
            other.index === option.index ||
            selected[other.index] === null ||
            variantValue(variant, other.index) === selected[other.index]
        )
    );
  }

  #openSheet(productId) {
    const product = this.#products.get(productId);
    if (!product) return;
    const options = this.#optionsFor(product);
    const selected = options.map((option) => (option.values.length === 1 ? option.values[0] : null));
    this.#sheetState = { product, options, selected };
    const sheet = this.querySelector('[data-reco-sheet]');
    const image = imageSource(product, 160);
    sheet.innerHTML = `
      <div class="cart-reco__sheet-head">
        <span class="cart-reco__sheet-media">${
          image ? `<img src="${escapeHtml(image)}" alt="" width="56" height="70">` : ''
        }</span>
        <div>
          <p class="cart-reco__sheet-title">${escapeHtml(product.title)}</p>
          <p class="cart-reco__sheet-price" data-sheet-price></p>
        </div>
        <button type="button" class="cart-reco__sheet-close button-unstyled" data-reco-sheet-close aria-label="Close">${CLOSE_ICON}</button>
      </div>
      ${options
        .map(
          (option) => `<fieldset class="cart-reco__group">
        <legend class="cart-reco__legend">${escapeHtml(option.name)}</legend>
        <div class="cart-reco__chips">${option.values
          .map(
            (value) =>
              `<button type="button" class="cart-reco__chip" data-reco-chip data-index="${option.index}" data-value="${escapeHtml(
                value
              )}" aria-pressed="false">${escapeHtml(value)}</button>`
          )
          .join('')}</div>
      </fieldset>`
        )
        .join('')}
      <button type="button" class="cart-reco__confirm" data-reco-confirm disabled></button>`;
    this.toggleAttribute('data-sheet-open', true);
    sheet.setAttribute('aria-hidden', 'false');
    this.#syncSheet();
    sheet.querySelector('[data-reco-chip]:not(:disabled)')?.focus({ preventScroll: true });
  }

  #closeSheet() {
    this.removeAttribute('data-sheet-open');
    this.querySelector('[data-reco-sheet]')?.setAttribute('aria-hidden', 'true');
    this.#sheetState = null;
  }

  #select(index, value) {
    const state = this.#sheetState;
    if (!state) return;
    state.selected[index] = state.selected[index] === value ? null : value;
    state.options.forEach((option) => {
      const current = state.selected[option.index];
      if (option.index !== index && current !== null && !this.#isAvailable(option, current)) {
        state.selected[option.index] = null;
      }
    });
    this.#syncSheet();
  }

  #syncSheet() {
    const state = this.#sheetState;
    if (!state) return;
    const sheet = this.querySelector('[data-reco-sheet]');
    sheet.querySelectorAll('[data-reco-chip]').forEach((chip) => {
      const option = state.options[Number(chip.dataset.index)];
      const value = chip.dataset.value;
      chip.disabled = !this.#isAvailable(option, value);
      chip.setAttribute('aria-pressed', String(state.selected[option.index] === value));
    });
    const variant = this.#matchVariant(state);
    const source = variant || state.product;
    sheet.querySelector('[data-sheet-price]').textContent = formatMoney(
      toCents(source.price ?? state.product.price),
      this.dataset.moneyFormat
    );
    const confirm = sheet.querySelector('[data-reco-confirm]');
    confirm.classList.remove('is-loading', 'is-done');
    if (variant && variant.available !== false) {
      confirm.disabled = false;
      confirm.textContent = this.dataset.addLabel || 'Add to cart';
      return;
    }
    const pending = state.options.find((option) => state.selected[option.index] === null);
    confirm.disabled = true;
    confirm.textContent = pending
      ? `${this.dataset.selectLabel || 'Select'} ${pending.name}`
      : this.dataset.soldOutLabel || 'Sold out';
  }

  async #confirmSheet(button) {
    const state = this.#sheetState;
    if (!state || button.disabled) return;
    const variant = this.#matchVariant(state);
    if (!variant || variant.available === false) return;
    const productId = state.product.id;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = this.dataset.addingLabel || 'Adding...';
    const added = await this.#addToCart(variant.id);
    if (!added) {
      button.textContent = this.dataset.errorText || 'Something went wrong';
      setTimeout(() => this.#syncSheet(), 1600);
      return;
    }
    button.classList.remove('is-loading');
    button.classList.add('is-done');
    button.textContent = this.dataset.addedLabel || 'Added';
    setTimeout(() => {
      this.#closeSheet();
      this.#removeCard(this.querySelector(`.cart-reco__card[data-product-id="${productId}"]`));
    }, 700);
  }

  #removeCard(card) {
    if (!card) return;
    card.classList.add('is-added');
    this.#signature = '';
    setTimeout(() => {
      card.remove();
      const list = this.querySelector('[data-reco-list]');
      if (!list.children.length) this.#showStatus(this.dataset.emptyText);
    }, 420);
  }

  async #add(button) {
    const variantId = Number(button.dataset.recoAdd);
    const card = button.closest('.cart-reco__card');
    if (!variantId || !card || card.classList.contains('is-adding')) return;
    card.classList.add('is-adding');
    const added = await this.#addToCart(variantId);
    if (!added) {
      card.classList.remove('is-adding');
      card.classList.add('has-error');
      setTimeout(() => card.classList.remove('has-error'), 1600);
      return;
    }
    this.#removeCard(card);
  }

  async #addToCart(variantId) {
    try {
      const response = await fetch(`${this.#root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: UPSELL_PROPERTIES }], sections: this.dataset.sectionId }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      await this.#refreshCart(data, variantId);
      return true;
    } catch (error) {
      return false;
    }
  }

  async #refreshCart(data, variantId) {
    try {
      const { CartAddEvent } = await import('@theme/events');
      this.dispatchEvent(
        new CartAddEvent({}, `cart-recommendations-${variantId}`, {
          source: 'cart-recommendations',
          itemCount: 1,
          sections: data.sections,
        })
      );
    } catch (error) {
      await this.#replaceDrawer(data.sections?.[this.dataset.sectionId]);
    }
  }

  async #replaceDrawer(html) {
    let markup = html;
    if (!markup) {
      const response = await fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`);
      markup = await response.text();
    }
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const next = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
    const current = this.#dialog?.querySelector('[data-hydration-key="cart-drawer-inner"]');
    if (next && current) current.replaceWith(next);
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}